/**
 * Card-product hydration for below-the-fold positions on collection pages.
 *
 * Detects minimal cards (snippets/card-product-minimal.liquid) via
 * IntersectionObserver with a responsive rootMargin. When a minimal
 * card approaches the viewport, fetches the full card-product HTML
 * from templates/product.card-product-hydrate.liquid and swaps it in.
 *
 * Init is gated on the window load event plus a 400ms post-load
 * delay, then yields to requestIdleCallback so the observer can't
 * possibly fire during the browser's LCP measurement window.
 * Concurrent fetches are capped so a fast scroller never opens more
 * than three sockets at once, keeping bandwidth headroom for image
 * loads. rootMargin is a uniform 200px on every viewport — that's
 * enough headroom to swap before a scroll wave reaches the card,
 * without prefetching cards the user may never see.
 *
 * After swap, calls window.mountProductCardSlider (shared with
 * card-product-slider.js) to re-mount the Splide carousel with the
 * same options as page-load sliders. Custom elements auto-init via
 * connectedCallback when appended. A card-hydrated CustomEvent fires
 * on document so Loox/Klaviyo or future apps can re-bind widgets.
 *
 * Telemetry: console.warn on hydration failures (HTTP non-OK or
 * fetch reject) but the minimal card stays in place so the page
 * remains functional.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window) || !('fetch' in window)) {
    return;
  }

  var ROOT_MARGIN = '1500px';
  var MAX_CONCURRENT = 6;
  var POST_LOAD_DELAY_MS = 200;

  var pendingFetches = new Map();
  var queuedTargets = [];
  var inFlight = 0;

  function reinitSplide(rootEl) {
    if (typeof window.Splide !== 'function') {
      return;
    }
    var sliders = rootEl.querySelectorAll('.splide.js-product-card-slider');
    sliders.forEach(function (el) {
      if (typeof window.mountProductCardSlider === 'function') {
        try {
          window.mountProductCardSlider(el);
        } catch (e) {
          console.warn('[card-hydrate] mountProductCardSlider failed', e);
        }
      } else if (!el.classList.contains('is-initialized')) {
        try {
          new window.Splide(el, {
            pagination: false,
            arrows: true,
            drag: true,
            type: 'loop',
          }).mount();
          el.classList.add('is-initialized');
        } catch (e) {
          console.warn('[card-hydrate] Splide mount failed', e);
        }
      }
    });
  }

  function refreshApps(rootEl) {
    document.dispatchEvent(
      new CustomEvent('card-hydrated', { detail: { card: rootEl } })
    );
  }

  function drainQueue() {
    while (inFlight < MAX_CONCURRENT && queuedTargets.length > 0) {
      hydrate(queuedTargets.shift());
    }
  }

  function hydrate(minimalEl) {
    var url = minimalEl.dataset.hydrateUrl;
    if (!url || pendingFetches.has(minimalEl)) {
      return;
    }

    if (inFlight >= MAX_CONCURRENT) {
      queuedTargets.push(minimalEl);
      return;
    }

    var controller = new AbortController();
    pendingFetches.set(minimalEl, controller);
    inFlight++;

    fetch(url, { signal: controller.signal, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }
        return res.text();
      })
      .then(function (html) {
        pendingFetches.delete(minimalEl);
        inFlight--;
        if (!minimalEl.isConnected) {
          drainQueue();
          return;
        }
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        var fullCard = wrapper.firstElementChild;
        if (!fullCard) {
          drainQueue();
          return;
        }
        minimalEl.replaceWith(fullCard);
        reinitSplide(fullCard);
        refreshApps(fullCard);
        drainQueue();
      })
      .catch(function (err) {
        pendingFetches.delete(minimalEl);
        inFlight--;
        drainQueue();
        if (err.name === 'AbortError') {
          return;
        }
        console.warn('[card-hydrate] fetch failed for ' + url, err);
      });
  }

  function observeAll(observer) {
    document
      .querySelectorAll('.card-minimal[data-hydrate-url]')
      .forEach(function (el) {
        observer.observe(el);
      });
  }

  function init() {
    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            hydrate(entry.target);
          }
        });
      },
      { rootMargin: ROOT_MARGIN }
    );

    observeAll(observer);

    var grid = document.getElementById('product-grid');
    if (grid && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        observeAll(observer);
      });
      mo.observe(grid, { childList: true, subtree: true });
    }
  }

  function deferredInit() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init, { timeout: 1000 });
    } else {
      setTimeout(init, 0);
    }
  }

  function scheduleAfterLoad() {
    setTimeout(deferredInit, POST_LOAD_DELAY_MS);
  }

  if (document.readyState === 'complete') {
    scheduleAfterLoad();
  } else {
    window.addEventListener('load', scheduleAfterLoad, { once: true });
  }
})();
